/**
 * HiringDecisionPage - Data-driven hiring decision dashboard
 */
import { useState, useEffect } from 'react';
import { UserPlus, Users, DollarSign, TrendingUp, Clock, Award, AlertTriangle, CheckCircle, XCircle, Calculator, Briefcase, PiggyBank, Target, ArrowRight, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

const HiringDecisionPage = () => {
  const [analysis, setAnalysis] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  
  // Simulation form
  const [form, setForm] = useState({
    salary: 3000,
    allowance: 300,
    position: '',
    department: ''
  });

  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/hiring/analysis`);
      setAnalysis(res.data);
    } catch (error) {
      console.error('Error fetching analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    try {
      setSimulating(true);
      const res = await axios.post(`${API}/hiring/simulate`, null, {
        params: {
          proposed_salary: form.salary,
          proposed_allowance: form.allowance,
          position: form.position,
          department: form.department
        }
      });
      setSimulation(res.data);
    } catch (error) {
      console.error('Error simulating:', error);
      alert('Gagal menjalankan simulasi');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700" />
      </div>
    );
  }

  const s = analysis?.summary || {};
  const rec = analysis?.recommendation || {};
  const work = analysis?.workload || {};
  const perf = analysis?.performance || {};
  const fin = analysis?.financials || {};

  const recColor = rec.color === 'green' ? 'bg-emerald-500' : rec.color === 'yellow' ? 'bg-amber-500' : 'bg-red-500';
  const recBorder = rec.color === 'green' ? 'border-emerald-500' : rec.color === 'yellow' ? 'border-amber-500' : 'border-red-500';

  return (
    <div className="space-y-6" data-testid="hiring-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <UserPlus className="w-7 h-7 text-red-700" /> Hiring Decision Dashboard
        </h1>
        <p className="text-gray-500 text-sm">Buat keputusan hiring berdasarkan data, bukan suka-suka</p>
      </div>

      {/* Main Recommendation Card */}
      <div className={`bg-white rounded-2xl border-2 ${recBorder} shadow-lg overflow-hidden`}>
        <div className={`${recColor} text-white p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {rec.color === 'green' ? <CheckCircle className="w-8 h-8" /> : 
               rec.color === 'yellow' ? <AlertTriangle className="w-8 h-8" /> : 
               <XCircle className="w-8 h-8" />}
              <div>
                <p className="text-sm opacity-90">Cadangan Sistem</p>
                <h2 className="text-2xl font-bold">{rec.decision}</h2>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">Skor Keputusan</p>
              <p className="text-3xl font-bold">{rec.score}/100</p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-700 mb-3">Justifikasi:</h3>
          <div className="space-y-2">
            {(rec.reasons || []).map((reason, idx) => (
              <p key={idx} className="text-sm text-gray-600">{reason}</p>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard 
          icon={Users} 
          label="Jumlah Staf" 
          value={s.total_staff} 
          color="blue"
        />
        <MetricCard 
          icon={DollarSign} 
          label="Kos Majikan/Bulan" 
          value={`RM ${(s.total_employer_cost || 0).toLocaleString()}`}
          subtext={`${s.payroll_to_revenue_ratio}% dari revenue`}
          color={s.payroll_to_revenue_ratio > 40 ? 'red' : s.payroll_to_revenue_ratio > 30 ? 'yellow' : 'green'}
        />
        <MetricCard 
          icon={TrendingUp} 
          label="Revenue/Employee" 
          value={`RM ${(s.revenue_per_employee || 0).toLocaleString()}`}
          color="emerald"
        />
        <MetricCard 
          icon={PiggyBank} 
          label="Cash Runway" 
          value={`${s.cash_runway_months} bulan`}
          color={s.cash_runway_months < 6 ? 'red' : s.cash_runway_months < 12 ? 'yellow' : 'green'}
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue vs Payroll Trend */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-red-700" /> Trend Pendapatan Bulanan
          </h3>
          {fin.monthly_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={fin.monthly_trend}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C41E3A" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#C41E3A" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `RM${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`RM ${v.toLocaleString()}`, 'Revenue']} />
                <Area type="monotone" dataKey="amount" stroke="#C41E3A" fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-center py-10">Tiada data</p>}
        </div>

        {/* Workload / Overtime Trend */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-red-700" /> Trend Overtime (Jam)
            <span className={`ml-auto px-2 py-1 text-xs rounded-full ${
              work.overtime_trend === 'increasing' ? 'bg-red-100 text-red-700' :
              work.overtime_trend === 'stable' ? 'bg-blue-100 text-blue-700' :
              'bg-emerald-100 text-emerald-700'
            }`}>
              {work.overtime_trend === 'increasing' ? '↑ Meningkat' :
               work.overtime_trend === 'stable' ? '→ Stabil' : '↓ Menurun'}
            </span>
          </h3>
          {work.overtime_by_month?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={work.overtime_by_month}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="hours" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Jam OT" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-center py-10">Tiada data overtime</p>}
        </div>
      </div>

      {/* Performance & Utilization */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Performance Distribution */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-red-700" /> Prestasi Staf
          </h3>
          {perf.distribution?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie 
                    data={perf.distribution} 
                    cx="50%" 
                    cy="50%" 
                    innerRadius={40} 
                    outerRadius={70} 
                    dataKey="count" 
                    nameKey="rating"
                  >
                    {perf.distribution.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1">
                {perf.distribution.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                      <span className="text-gray-600">{d.rating}</span>
                    </div>
                    <span className="font-medium">{d.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-gray-400 text-center py-10">Tiada data penilaian</p>}
        </div>

        {/* Utilization Gauge */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-700" /> Staff Utilization
          </h3>
          <div className="flex flex-col items-center justify-center h-48">
            <div className="relative">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle cx="64" cy="64" r="56" stroke="#E5E7EB" strokeWidth="12" fill="none" />
                <circle 
                  cx="64" cy="64" r="56" 
                  stroke={s.utilization_rate > 90 ? '#EF4444' : s.utilization_rate > 75 ? '#F59E0B' : '#10B981'}
                  strokeWidth="12" 
                  fill="none"
                  strokeDasharray={`${(s.utilization_rate || 0) * 3.52} 352`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold">{Math.round(s.utilization_rate || 0)}%</span>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500">
              {s.utilization_rate > 90 ? 'Overworked - Perlu bantuan!' :
               s.utilization_rate > 75 ? 'Tinggi - Monitor closely' :
               'Normal - Capacity masih ada'}
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-red-700" /> Ringkasan
          </h3>
          <div className="space-y-4">
            <StatRow label="High Performers" value={perf.high_performers || 0} total={s.total_staff} color="emerald" />
            <StatRow label="Low Performers" value={perf.low_performers || 0} total={s.total_staff} color="red" />
            <StatRow label="Dinilai" value={perf.evaluated_count || 0} total={s.total_staff} color="blue" />
            <div className="pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Purata Gaji</span>
                <span className="font-medium">RM {Math.round((s.total_payroll || 0) / Math.max(s.total_staff, 1)).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hiring Cost Simulator */}
      <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-2xl p-6 border border-red-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-red-700" /> Hiring Cost Simulator
        </h3>
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-700 mb-4">Maklumat Hire Baru</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Gaji Asas (RM)</label>
                <input 
                  type="number" 
                  value={form.salary} 
                  onChange={e => setForm({...form, salary: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Elaun (RM)</label>
                <input 
                  type="number" 
                  value={form.allowance} 
                  onChange={e => setForm({...form, allowance: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Jawatan</label>
                <input 
                  type="text" 
                  value={form.position} 
                  onChange={e => setForm({...form, position: e.target.value})}
                  placeholder="cth: Marketing Executive"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Jabatan</label>
                <input 
                  type="text" 
                  value={form.department} 
                  onChange={e => setForm({...form, department: e.target.value})}
                  placeholder="cth: Marketing"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <button 
              onClick={runSimulation}
              disabled={simulating}
              className="mt-4 w-full py-3 bg-red-700 text-white rounded-lg font-medium hover:bg-red-800 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {simulating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Mengira...
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4" /> Kira Impak
                </>
              )}
            </button>
          </div>

          {/* Simulation Results */}
          {simulation && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h4 className="font-semibold text-gray-700 mb-4">Hasil Simulasi</h4>
              
              {/* New Hire Cost Breakdown */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-500 mb-2">Kos Hire Baru (Bulanan)</p>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-2xl font-bold text-red-700">
                      RM {simulation.new_hire.total_employer_cost.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      Gaji Bersih: RM {simulation.new_hire.net_salary.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <p>EPF (Majikan): RM {simulation.new_hire.epf_employer}</p>
                    <p>SOCSO: RM {simulation.new_hire.socso_employer}</p>
                    <p>EIS: RM {simulation.new_hire.eis_employer}</p>
                  </div>
                </div>
              </div>

              {/* Before vs After */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Sekarang</p>
                  <p className="text-lg font-bold">{simulation.current_state.staff_count} staf</p>
                  <p className="text-xs text-gray-500">
                    Ratio: {simulation.current_state.payroll_ratio}%
                  </p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs text-gray-500">Selepas Hire</p>
                  <p className="text-lg font-bold">{simulation.after_hiring.staff_count} staf</p>
                  <p className="text-xs text-red-600">
                    Ratio: {simulation.after_hiring.payroll_ratio}%
                  </p>
                </div>
              </div>

              {/* Impact Summary */}
              <div className={`rounded-lg p-3 ${
                simulation.impact.impact_level === 'high' ? 'bg-red-50 border border-red-200' :
                simulation.impact.impact_level === 'medium' ? 'bg-amber-50 border border-amber-200' :
                'bg-emerald-50 border border-emerald-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {simulation.impact.impact_level === 'high' ? <AlertTriangle className="w-4 h-4 text-red-600" /> :
                   simulation.impact.impact_level === 'medium' ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
                   <CheckCircle className="w-4 h-4 text-emerald-600" />}
                  <p className="font-medium text-sm">
                    Impak: {simulation.impact.impact_level === 'high' ? 'TINGGI' :
                            simulation.impact.impact_level === 'medium' ? 'SEDERHANA' : 'RENDAH'}
                  </p>
                </div>
                <div className="space-y-1 text-xs">
                  <p className="flex justify-between">
                    <span className="text-gray-600">Kos Tahunan:</span>
                    <span className="font-medium">RM {simulation.impact.annual_cost_increase.toLocaleString()}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-gray-600">Runway berkurang:</span>
                    <span className="font-medium">{simulation.impact.runway_reduction} bulan</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-gray-600">Break-even Revenue:</span>
                    <span className="font-bold text-red-700">+RM {simulation.impact.break_even_revenue.toLocaleString()}/bln</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper Components
const MetricCard = ({ icon: Icon, label, value, subtext, color = 'gray' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    yellow: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700'
  };

  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
      {subtext && <p className="text-xs opacity-70 mt-1">{subtext}</p>}
    </div>
  );
};

const StatRow = ({ label, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  const colorClasses = {
    emerald: 'bg-emerald-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500'
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium">{value}/{total}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} rounded-full transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default HiringDecisionPage;
